import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const ERROR_TOAST_DEDUPE_MS = 4000;

/**
 * Centralized Axios Interceptor Component
 * Handles 401 (Unauthorized) and 403 (Forbidden) errors globally
 * Shows toast notifications for 403 errors with context about what the user was trying to access
 */
const AxiosInterceptor = ({ children }) => {
  const { setUser } = useAuth();
  const { showToast } = useToast();
  const recentErrorToastsRef = useRef(new Map());

  useEffect(() => {
    const shouldShowErrorToast = (message) => {
      const now = Date.now();
      const lastShownAt = recentErrorToastsRef.current.get(message) || 0;
      if (now - lastShownAt < ERROR_TOAST_DEDUPE_MS) return false;
      recentErrorToastsRef.current.set(message, now);
      return true;
    };

    // Add response interceptor to handle errors globally
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.config?.skipGlobalErrorToast) {
          return Promise.reject(error);
        }

        const status = error.response?.status;
        const config = error.config;
        const url = config?.url || '';
        const method = config?.method?.toUpperCase() || 'GET';

        // Extract meaningful context from the URL
        const getActionContext = (url, method) => {
          if (!url) return 'this action';
          
          // Extract endpoint and entity from URL
          const urlParts = url.split('/').filter(Boolean);
          const apiIndex = urlParts.indexOf('api');
          
          if (apiIndex >= 0 && apiIndex < urlParts.length - 1) {
            const module = urlParts[apiIndex + 1]; // e.g., 'sales', 'ops', 'audit', 'crew'
            const endpoint = urlParts[apiIndex + 2] || ''; // e.g., 'trips', 'sectors', 'pilots'
            
            // Map modules to user-friendly names
            const moduleNames = {
              'sales': 'Flight Entry',
              'ops': 'Operations Board',
              'audit': 'Audit Logs',
              'crew': 'Crew Inbox',
              'aircraft': 'Aircraft',
              'maintenance': 'Maintenance',
              'users': 'User Management',
              'notifications': 'Notifications'
            };
            
            // Map endpoints to actions
            const actionMap = {
              'GET': 'view',
              'POST': 'create',
              'PUT': 'update',
              'DELETE': 'delete',
              'PATCH': 'update'
            };
            
            const action = actionMap[method] || 'access';
            const moduleName = moduleNames[module] || module;
            
            if (endpoint) {
              // Try to extract entity type from endpoint
              const entityMap = {
                'trips': 'charter',
                'sectors': 'sector',
                'pilots': 'pilot',
                'aircraft': 'aircraft',
                'requirements': 'flight requirements',
                'passengers': 'passengers',
                'assign': 'assignment',
                'charter': 'charter audit logs',
                'sector': 'sector audit logs'
              };
              
              const entity = entityMap[endpoint] || endpoint;
              return `${action} ${entity} in ${moduleName}`;
            }
            
            return `${action} ${moduleName}`;
          }
          
          return 'this action';
        };

        // Handle 401 Unauthorized (Token expired/invalid)
        if (status === 401) {
          const isAuthEndpoint = url.includes('/api/auth/');
          const isLoginPage = window.location.pathname.startsWith('/login');
          
          if (!isAuthEndpoint && !isLoginPage) {
            showToast(
              'Your session has expired. Please log in again.',
              'error',
              6000
            );
            // Token expired or invalid - clear auth data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('tokenExpiry');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
            // Only redirect if not already on login page
            window.location.href = '/login';
          }
        }
        
        // Handle 403 Forbidden (Permission denied)
        else if (status === 403) {
          const actionContext = getActionContext(url, method);
          const errorMessage = error.response?.data?.message;
          const message = `You do not have permission to ${actionContext}. ${errorMessage ? `(${errorMessage})` : 'Please contact your administrator or Operations team if you need access.'}`;
          if (shouldShowErrorToast(message)) {
            showToast(message, 'warning', 8000);
          }
        }
        
        // Handle 429 Too Many Requests
        else if (status === 429) {
          const message = 'Too many requests. Please wait a moment before trying again.';
          if (shouldShowErrorToast(message)) {
            showToast(message, 'warning', 5000);
          }
        }
        
        // Handle 500+ Server Errors
        else if (status >= 500) {
          const actionContext = getActionContext(url, method);
          const message = `Server error while trying to ${actionContext}. Please try again later or contact support.`;
          if (shouldShowErrorToast(message)) {
            showToast(message, 'error', 6000);
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [showToast, setUser]);

  return children;
};

export default AxiosInterceptor;

